import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale, isAdmin } from '@components/frame';

import LineButton from '@components/common/lineButton';
import EditableLine from '@components/common/editableLine';
import EditField from '@components/common/editField';
import Line from '@components/common/line-new';
import Loading from '@components/loading';
import FeatureFlags from '@components/featureFlags';

import ColorWrapper from '@styles/colors/index';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';
import { getBoxShadowStyle } from '@styles/shadow';

import Header from '@components/settings/v2/common/SettingsHeader';
import { BACK_BUTTON_SETTINGS_TEXT, SCREEN_BORDER_RADIUS, SCREEN_PADDING_HORIZONTAL, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import TouchableElement from '@components/common/touchableElement';

import { useManageCompany } from './hooks/useManageCompany';

const ValueLine = ({ C, value, onClick }) => {
  return (
    <TouchableElement
      onPress={() => {
        onClick();
      }}
    >
      {({ pressHoverColor, boxStyle }) => (
        <View
          style={[
            {
              paddingHorizontal: scale(15),
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'row',
              height: scale(41.5),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
              backgroundColor: C(pressHoverColor('Background / Light / Field')),
            },
            boxStyle,
          ]}
        >
          <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1 }]}>
            <View>
              <Text
                style={[
                  {
                    color: C(pressHoverColor('Label/Light/Primary')),
                    fontVariant: ['tabular-nums'],
                  },
                  font.Body,
                ]}
              >
                {i18n.t(value)}
              </Text>
            </View>
            <View>
              <Svg icon="chevronRightLight" color="Label/Light/Quinary" width={scale(7)} height={scale(12)} />
            </View>
          </View>
        </View>
      )}
    </TouchableElement>
  );
};

const Company = (props) => {
  const { C } = props;

  const {
    companyProfile,
    isCompanyRemoving,
    notifyOnMachineErrors,
    factoryTimeZone,
    factoryLanguageDisplay,
    notificationLoading,
    triggerNotification,
    showEditField,
    editFieldConfig,
    toggleEditField,
    openFactoryTimePicker,
    openFactoryLanguagePicker,
    companyList,
    openProfileFieldEditor,
    removeAlert,
  } = useManageCompany();

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  if (isCompanyRemoving) {
    return (
      <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignContent: 'center', alignItems: 'center' }}>
        <Loading {...{ background: C('Background/Light/Detail') }} />
      </View>
    );
  }

  return [
    <EditField
      key="EditField"
      {...{
        C,
        showModal: showEditField,
        onClose: toggleEditField,
        ...editFieldConfig,
      }}
    />,
    <View key="Company" style={{ width: '100%', height: '100%', backgroundColor: C('Background/Light/Detail'), alignItems: 'center' }}>
      <Header backBtnText={BACK_BUTTON_SETTINGS_TEXT} name={'Company'} key={'CompanyHeader'} />
      <ScrollView style={{ width: '100%', flex: 1 }} contentContainerStyle={{ paddingBottom: scale(50) }}>
        <View
          style={{
            paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
          }}
        >
          <View>
            <View style={{ paddingLeft: scale(15), marginBottom: scale(5) }}>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('NOTIFICATION')}</Text>
            </View>
            <View testID={TEST_IDS.settingsScreen.companyNotifyToggle} style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              <EditableLine
                {...{
                  item: {
                    value: notifyOnMachineErrors,
                    key: 'switchedOn',
                    name: 'Notify on machine errors',
                    noArrow: true,
                    isLable: true,
                    oneLine: true,
                    small: false,
                    width: 270,
                    type: 'boolean',
                    loading: notificationLoading,
                    onChange: (v) => triggerNotification(v),
                  },
                  index: 0,
                  array: [{}],
                  C,
                  topBorder: false,
                  bottomBorder: false,
                  roundTop: true,
                  roundBottom: true,
                }}
              />
            </View>
            <View style={{ paddingHorizontal: scale(15), marginTop: scale(6) }}>
              <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
                {i18n.t('All users of your organization will be notified if a machine has an error while running in automatic mode')}
              </Text>
            </View>

            <View style={{ paddingLeft: scale(15), marginTop: scale(26), marginBottom: scale(5) }}>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('FACTORY TIME ZONE')}</Text>
            </View>
            <View testID={TEST_IDS.settingsScreen.companyTimezoneRow} style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              <ValueLine
                {...{
                  C,
                  value: factoryTimeZone,
                  onClick: openFactoryTimePicker,
                }}
              />
            </View>

            <View style={{ paddingLeft: scale(15), marginTop: scale(26), marginBottom: scale(5) }}>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('FACTORY LANGUAGE')}</Text>
            </View>
            <View testID={TEST_IDS.settingsScreen.companyLanguageRow} style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              <ValueLine
                {...{
                  C,
                  value: factoryLanguageDisplay,
                  onClick: openFactoryLanguagePicker,
                }}
              />
            </View>
            <View style={{ paddingHorizontal: scale(15), marginTop: scale(6) }}>
              <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
                {i18n.t('Invites to colleagues will be sent in this language.')}
              </Text>
            </View>

            <View style={{ paddingLeft: scale(15), marginTop: scale(26), marginBottom: scale(5) }}>
              <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('COMPANY PROFILE')}</Text>
            </View>
            <View testID={TEST_IDS.settingsScreen.companyProfileSection} style={[shadowStyles, { borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
              {companyList.map((item, index) => (
                <Line
                  {...{
                    alwaysTwoLines: true,
                    item: {
                      ...item,
                      onClick: () => {
                        if (item.onClick) {
                          return item.onClick();
                        }
                        openProfileFieldEditor(item);
                      },
                      value: companyProfile[item.value],
                    },
                    index: index,
                    array: companyList,
                    C,
                    roundTop: index === 0,
                    roundBottom: companyList.length - 1 === index,
                  }}
                  key={item.value}
                />
              ))}
            </View>

            <FeatureFlags {...{ name: 'show-delete-company-command' }}>
              {isAdmin && (
                <View>
                  <View style={{ paddingLeft: scale(15), marginTop: scale(26), marginBottom: scale(5) }}>
                    <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>{i18n.t('DELETE')}</Text>
                  </View>
                  <View testID={TEST_IDS.settingsScreen.companyDeleteButton} style={{ width: '100%' }}>
                    <LineButton
                      {...{
                        onPress: removeAlert,
                        text: 'Delete my Company',
                        color: 'Chart/Light/Warning',
                        C,
                      }}
                    />
                  </View>
                  <View style={{ paddingHorizontal: scale(15), marginTop: scale(6), marginBottom: scale(20) }}>
                    <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary') }]}>
                      {i18n.t('Deleting your company means that all data stored in the cloud and locally will get lost!')}
                    </Text>
                    <Text style={[font['Caption 1'], { color: C('Label/Light/Tertiary'), marginTop: scale(20), fontWeight: '700' }]}>
                      {i18n.t('Delete cannot be undone!')}
                    </Text>
                  </View>
                </View>
              )}
            </FeatureFlags>
          </View>
        </View>
      </ScrollView>
    </View>,
  ];
};

export default ColorWrapper(Company);
